import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { ReactComponent as ViewIcon } from "../../../assets/icons/eye.svg";
import { ReactComponent as EditIcon } from "../../../assets/icons/message-edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/trash.svg";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/search-normal.svg";
import { ReactComponent as NoDataTile } from "../../../assets/icons/no-data.svg";
import "./addons.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeletePopup from "../../../components/deletePopup/deletePopup";
import ViewPopupWrapper from "../../../components/viewPopupWrapper/viewPopupWrapper";
import PaginationComponent from "../../../components/pagination/pagination";
import ApiLoader from "../../../components/apiLoader/apiLoader";
import {
  fetchAddonDetailsAsync,
  fetchAddonsAsync,
} from "../../../redux/reducers/addonsSlice";
import { fetchDeviceDetailsAsync } from "../../../redux/reducers/devicesSlice";

const TableHeader = React.memo(() => {
  const headers = ["Name", "State", "Description", "Action"];
  return (
    <TableHead>
      <TableRow className="plans-header-row">
        {headers.map((header, index) => (
          <TableCell
            key={header}
            className={`plans-table-header ${
              index === 0 ? "plans-table-header-first-col" : ""
            }${index === 4 ? "plans-table-header-last-col" : ""}`}
          >
            {header}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

const TableRowData = React.memo(
  ({ row, setIsViewPopupVisible, refreshList }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);

    const handleViewClick = () => {
      dispatch(fetchAddonDetailsAsync(row.id));
      setIsViewPopupVisible(true);
    };

    const handleEditClick = () => {
      navigate(`/edit-addons/${row.id}`);
    };

    const handleDeletePopupOpen = () => {
      setIsDeletePopupVisible(true);
    };
    const handleDeletePopupClose = () => {
      setIsDeletePopupVisible(false);
    };
    return (
      <TableRow className="plans-table-row">
        <TableCell>{row.planName}</TableCell>
        <TableCell>{row.state}</TableCell>
        <TableCell>{row.description}</TableCell>
        <TableCell className="plans-menu-items">
          <IconButton onClick={handleViewClick}>
            <ViewIcon />
          </IconButton>
          <IconButton onClick={handleEditClick}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={handleDeletePopupOpen}>
            <DeleteIcon />
            {isDeletePopupVisible && (
              <DeletePopup
                onClose={handleDeletePopupClose}
                id={row?.id}
                name={"device"}
                refreshList={refreshList}
              />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }
);

const Addons = () => {
  const [isViewPopupVisible, setIsViewPopupVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchValue);

  const dispatch = useDispatch();
  const { addonsData, isLoading, addonDetails } = useSelector((state) => ({
    addonsData: state?.addonsData?.addonsData,
    addonDetails: state?.addonsData?.selectedAddon,
    isLoading: state?.addonsData?.isAddonsLoading,
  }));
  const navigate = useNavigate();
  const totalPages = Math.round(addonsData?.total_count / rowsPerPage);

  const handleCreateAddon = () => {
    navigate("/create-addons");
  };

  const handleChange = (event) => {
    setSearchValue(event.target.value);
  };

  useEffect(() => {
    // Set a delay before updating
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  useEffect(() => {
    dispatch(
      fetchAddonsAsync({
        page,
        limit: rowsPerPage,
        searchtext: debouncedSearch,
      })
    );
  }, [dispatch, page, rowsPerPage, debouncedSearch]);

  const refreshAddonsList = () => {
    dispatch(
      fetchAddonsAsync({
        page,
        limit: rowsPerPage,
        searchtext: debouncedSearch,
      })
    );
  };

  const formattedaddonsData = addonsData?.addons?.map((plan) => ({
    id: plan.id,
    planName: plan.name,
    state: plan.state_code,
    description: plan.description,
  }));

  const formattedViewAddonDetails = [
    [
      {
        title: "Name",
        value: addonDetails?.name,
        cols: 1,
        alignment: "left",
      },
      {
        title: "Description",
        value: addonDetails?.description,
        cols: 1,
        alignment: "center",
      },
      {
        title: "State",
        value: addonDetails?.state_code,
        cols: 1,
        alignment: "left",
      },
    ],
    ,
    ...(addonDetails?.addonRates?.length
      ? addonDetails.addonRates.map((addonRate, index, array) => [
          {
            title: "Min device price",
            value: `$${addonRate?.min_device_price}`,
            cols: 1,
            alignment: "left",
            isLastRow: index === array.length - 1,
          },
          {
            title: "Max device price",
            value: `$${addonRate?.max_device_price}`,
            cols: 1,
            alignment: "center",
            isLastRow: index === array.length - 1,
          },
          {
            title: "Price",
            value: `$${parseFloat(addonRate?.price || 0).toFixed(2)}`,
            cols: 1,
            alignment: "right",
            isLastRow: index === array.length - 1,
          },
        ])
      : []),
  ];

  const closeViewModal = () => {
    setIsViewPopupVisible(false);
  };

  return (
    <div className="plans-table">
      <ApiLoader loading={isLoading} />

      <ViewPopupWrapper
        isOpen={isViewPopupVisible}
        onClose={closeViewModal}
        title="Addon Details"
        columns={3}
        rows={formattedViewAddonDetails}
      />
      <div className="header-section">
        <p className="plans-title">Addons</p>
      </div>
      <div className="sub-header-section">
        <TextField
          variant="outlined"
          placeholder="Search Addons"
          fullWidth
          value={searchValue}
          onChange={handleChange}
          sx={{
            borderRadius: "50px",
            border: "1px solid #EBEEF2",
            width: "40%",
            height: "40px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "25px",
              paddingLeft: "12px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiInputBase-input": {
              color: "#A3AAB2",
              padding: 0,
              height: "40px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <button onClick={handleCreateAddon} className="create-plan-btn">
          Create Addons
          <PlusIcon />
        </button>
      </div>
      <Paper className="plans-table-paper">
        <TableContainer>
          <Table>
            <TableHeader />
            <TableBody>
              {formattedaddonsData?.length > 0 ? (
                formattedaddonsData?.map((plan, index) => (
                  <TableRowData
                    key={index}
                    row={plan}
                    setIsViewPopupVisible={setIsViewPopupVisible}
                    refreshList={refreshAddonsList}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <div className="no-data-admin">
                      <NoDataTile />
                    </div>{" "}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <div className="device-pagination">
        {totalPages > 0 && (
          <PaginationComponent
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </div>
    </div>
  );
};

export default Addons;
